import React, { useState, useEffect } from 'react';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Button, Menu, MenuItem, CircularProgress, Snackbar, Slide, makeStyles } from '@material-ui/core';

import BrandDetail from './BrandDetail';
import BrandProfile from './BrandProfile';
import BrandCustomize from './BrandCustomize';
import BrandDelete from './BrandDelete';

import { getBrands } from '../../stores/dataorch';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    minHeight: 'calc(100vh - 234px)',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(2),
    height: '60px',
  },
  head__title: {
    color: '#3b3b3b',
    margin: 0,
    fontFamily: 'AvenirNext-DemiBold',
  },
  table__cell: {
    fontSize: '12px',
  },
  btn: {
    fontFamily: 'AvenirNext-DemiBold',
  },
}));

const columns = [
  { name: 'ID', width: 30 },
  { name: 'BRAND NAME', width: 85 },
  { name: 'SUPPORT EMAIL', width: 95 },
  { name: 'SUPPORT PHONE NO', width: 125 },
  { name: 'CODE', width: 75 },
  { name: '', width: 55 },
];

function BrandList() {
  const classes = useStyles();

  const [brands, setBrands] = useState([]);

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [alert, setAlert] = useState({});

  const [openDialog, setOpenDialog] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [openCustomize, setOpenCustomize] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedBrand(null);
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setAlert({});
  };

  const handleSelected = (e, brand) => {
    setAnchorEl(e.currentTarget);
    setSelectedBrand(brand);
  };

  const handleOpenProfile = () => {
    setAnchorEl(null);
    setOpenProfile(true);
  };

  const handleOpenEdit = () => {
    setAnchorEl(null);
    setOpenDialog(true);
  };

  const handleOpenCustomize = () => {
    setAnchorEl(null);
    setOpenCustomize(true);
  };

  const handleOpenDelete = () => {
    setAnchorEl(null);
    setOpenDelete(true);
  };

  const closeAllModal = () => {
    setOpenDialog(false);
    setOpenDelete(false);
    setOpenCustomize(false);
    setOpenProfile(false);
    setSelectedBrand(null);
  };

  const handleSubmitSuccess = (message) => {
    closeAllModal();
    setAlert({ severity: 'success', message });
    getBrands()
      .then((response) => response.data && setBrands(response.data))
      .catch((error) => setAlert({ severity: 'error', message: error.message || error.toString() }));
  };

  const handleSubmitCustomize = (message) => {
    closeAllModal();
    setAlert({ severity: 'success', message });
  };

  const handleOpenTrackingPage = () => {
    setOpenProfile(false);
    setOpenCustomize(true);
  };

  useEffect(() => {
    setLoading(true);
    getBrands()
      .then((response) => response.data && setBrands(response.data))
      .catch((error) => setAlert({ severity: 'error', message: error?.message || error.toString() }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <h2 className={`${classes.head__title}`}>Brands</h2>
        <Button variant="contained" color="primary" type="button" size="medium" className={classes.btn} onClick={() => setOpenDialog(true)}>
          <span>Add Brand</span>
        </Button>
      </div>
      <TableContainer component="div">
        <Table aria-label="brands" size="small" component="table" style={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {columns.map(({ name, width }) => (
                <TableCell key={name} className={classes.table__cell} width={width}>
                  {name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} rowSpan={2} align="center" height={200}>
                  <CircularProgress size={50} />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {brands.length === 0 && (
                  <TableRow height={40}>
                    <TableCell colSpan={6} align="center">
                      Empty data
                    </TableCell>
                  </TableRow>
                )}
                {brands.map((brand) => (
                  <TableRow hover key={brand.id}>
                    <TableCell className={classes.table__cell}>{brand.id}</TableCell>
                    <TableCell className={classes.table__cell}>{brand.name}</TableCell>
                    <TableCell className={classes.table__cell}>{brand.email}</TableCell>
                    <TableCell className={classes.table__cell}>{brand.phone_number}</TableCell>
                    <TableCell className={classes.table__cell}>{brand.code}</TableCell>
                    <TableCell className={classes.table__cell}>
                      <Button size="small" onClick={(e) => handleSelected(e, brand)}>
                        <MoreHorizIcon fontSize="small" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleOpenProfile}>View Brand Profile</MenuItem>
        <MenuItem onClick={handleOpenEdit}>Edit Brand</MenuItem>
        <MenuItem onClick={handleOpenCustomize}>Customize Brand</MenuItem>
        <MenuItem onClick={handleOpenDelete}>
          <span style={{ color: '#ad0418' }}>Delete Brand</span>
        </MenuItem>
      </Menu>
      {openDialog && <BrandDetail selectedBrandId={selectedBrand?.id} onSuccess={handleSubmitSuccess} onClose={closeAllModal} />}
      {openProfile && <BrandProfile selectedBrandId={selectedBrand?.id} onOk={closeAllModal} onClose={closeAllModal} handleOpenTrackingPage={handleOpenTrackingPage} />}
      {openCustomize && <BrandCustomize selectedBrandId={selectedBrand?.id} onOk={handleSubmitCustomize} onClose={closeAllModal} />}
      {openDelete && <BrandDelete onOk={handleSubmitSuccess} selectedBrand={selectedBrand} onClose={closeAllModal} />}
      <Snackbar
        open={Boolean(Object.keys(alert).length)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleCloseSnackbar}
        TransitionComponent={Slide}
        message={alert.message}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
}

export default BrandList;
